import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { ItemStats } from '@/features/items/components/ItemStats'
import { QRCodeManager } from '@/features/qrcode/components/QRCodeManager'
import { useConfirm } from '@/hooks/useConfirm'
import { getMutationOptions } from '@/utils/mutation'
import { TabsContent } from '@radix-ui/react-tabs'
import { Bed as BedIcon, Edit2, Monitor, Plus, QrCode, Trash2, UserSearch } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteBed } from '../hooks/useBed'
import { Bed } from '../types/bed'
import { BedDetailDialog } from './BedDetailDialog'
import { BedDialog } from './BedDialog'

export function BedList({ departmentId, beds = [] }: { departmentId: string; beds?: Bed[] }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { isAdmin } = useAuth()
  const deleteBed = useDeleteBed()
  const [selectedBedId, setSelectedBedId] = useState<string | null>(null)
  const [bedOpen, setBedOpen] = useState(false)
  const [bedDetailOpen, setBedDetailOpen] = useState(false)

  const [activeTab, setActiveTab] = useState<'beds' | 'qrcodes'>('beds')

  const canManage = isAdmin

  const handleDeleteBed = async (bedId: string) => {
    const name = beds.find(i => i.id === bedId)?.name
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('bed.confirmDeleteDescription', { name }),
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('bed.toast.deleteSuccess'),
      })
      deleteBed.mutate({ departmentId, id: bedId }, options)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'beds' | 'qrcodes')}>
        <SectionCard
          data-testid="department-bed-list"
          header={
            <div className="flex gap-4">
              {beds?.length === 0 ? (
                <span className="text-sm font-semibold">{t('common.bed')}</span>
              ) : (
                <div className="flex w-full justify-center sm:justify-start">
                  <TabsList className="flex flex-col w-full h-full items-start sm:flex-row">
                    <TabsTrigger value="beds" className="text-xs w-full justify-center">
                      <BedIcon className="mr-1 size-3" />
                      {t('common.beds')}
                    </TabsTrigger>
                    <TabsTrigger value="qrcodes" className="text-xs w-full justify-center">
                      <QrCode className="mr-1 size-3" />
                      {t('common.qrcodes')}
                    </TabsTrigger>
                  </TabsList>
                </div>
              )}
            </div>
          }
          right={
            <>
              {canManage && activeTab === 'beds' && (
                <div className={'flex justify-end'}>
                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    data-testid="bed-add-button"
                    onClick={() => {
                      setSelectedBedId(null)
                      setBedOpen(true)
                    }}
                  >
                    <Plus className="size-3" />
                    {t('bed.add')}
                  </Button>
                </div>
              )}
            </>
          }
        >
          <TabsContent value="beds" className="mt-0">
            {beds?.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">{t('department.noBed')}</p>
            ) : (
              <div className="flex flex-col gap-4 ">
                {beds?.map(bed => {
                  const items = bed.items
                  return (
                    <SectionCard
                      key={bed.id}
                      data-testid="bed-card"
                      data-bed-id={bed.id}
                      onClick={() => {
                        setSelectedBedId(bed.id)
                        setBedDetailOpen(true)
                      }}
                      cardClass="cursor-pointer border-border/50 bg-card transition-all hover:border-primary/30"
                      headerClass="pb-0"
                      header={
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <BedIcon className="size-4 text-primary" />
                            <span className="text-sm " data-testid="bed-card-name">
                              {t('common.bed')} {bed.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <UserSearch className="size-3" />
                              <span data-testid="bed-card-patient">{bed?.patientName ?? '-'}</span>
                            </span>
                          </div>
                        </div>
                      }
                      right={
                        <>
                          {canManage && (
                            <Button
                              variant={'outline'}
                              size="sm"
                              className="h-6"
                              data-testid="bed-card-edit"
                              onClick={e => {
                                e.stopPropagation()
                                setSelectedBedId(bed.id)
                                setBedOpen(true)
                              }}
                            >
                              <Edit2 className="size-3" />
                            </Button>
                          )}
                          {canManage && (
                            <div className="flex justify-end">
                              <Button
                                variant={'outline'}
                                size="sm"
                                className="h-6 border-destructive/30 text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid="bed-card-delete"
                                onClick={e => {
                                  e.stopPropagation()
                                  handleDeleteBed(bed.id)
                                }}
                              >
                                <Trash2 className="size-3" />
                              </Button>
                            </div>
                          )}
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            <Monitor className="mr-1 size-3" /> {items?.length}
                          </Badge>
                        </>
                      }
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {items?.map(item => {
                          const title = item.productName || `${item.productBrand} ${item.productModel}`
                          return (
                            <ItemStats
                              key={item.id}
                              title={title}
                              subtitle={item.itemSerialNumber}
                              days={item.daysSinceLastStatusChange}
                            ></ItemStats>
                          )
                        })}
                      </div>
                    </SectionCard>
                  )
                })}
              </div>
            )}
          </TabsContent>
          <TabsContent value="qrcodes" className="mt-0">
            <QRCodeManager
              data={beds}
              type="beds"
              path={`bed/${departmentId}`}
              getId={i => i.id}
              renderInfo={bed => {
                return (
                  <>
                    <span className="text-xs font-semibold">{bed?.name}</span>

                    <span className="flex items-center gap-1 font-mono text-[11px] text-primary">
                      <BedIcon className="size-2.5" />
                      {bed?.departmentName}
                    </span>
                  </>
                )
              }}
              getPrintLabel={bed => ({
                title: bed?.name ?? '',
                subtitle: bed?.departmentName ?? '',
              })}
            />
          </TabsContent>
        </SectionCard>
      </Tabs>

      <BedDialog
        open={bedOpen}
        onOpenChange={setBedOpen}
        departmentId={departmentId}
        bedId={selectedBedId ?? undefined}
      />
      <BedDetailDialog
        open={bedDetailOpen}
        onOpenChange={setBedDetailOpen}
        departmentId={departmentId}
        bedId={selectedBedId!}
      ></BedDetailDialog>
    </div>
  )
}
